import { useAuthStore } from '@/@vxapp/Auth/useAuthStore'
import { Action } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { subject as castSubject } from '@casl/ability'

const log = createLogger('casl', 'error')

export const canCreate = subject => {
  return can(Action.CREATE, subject)
}

export const canUpdate = subject => {
  return can(Action.UPDATE, subject)
}

export const canDelete = subject => {
  return can(Action.DELETE, subject)
}

export const canRead = subject => {
  return can(Action.READ, subject)
}

export const can = (action, subject) => {
  const auth = useAuthStore()
  const ability = auth.getAbility()
  const hasAbility = ability?.can(action, subject) ?? false

  if (!hasAbility) {
    log.debug(`can -> no ability for ${action} ${subject}`)
  }

  return hasAbility
}

export const canRecord = (action, subject, record, field) => {
  if (!record || typeof record !== 'object' || Array.isArray(record)) return false
  const ability = useAuthStore().getAbility()

  return ability?.can(action, castSubject(subject, { ...record }), field) ?? false
}

export const canRecordChange = (action, subject, before, after, field) => {
  if (
    !before ||
    !after ||
    typeof before !== 'object' ||
    typeof after !== 'object' ||
    Array.isArray(before) ||
    Array.isArray(after)
  )
    return false
  const ability = useAuthStore().getAbility()
  if (!ability) return false
  const oldRecord = castSubject(subject, { ...before })
  const candidate = castSubject(subject, { ...after })
  if (!ability.can(action, oldRecord, field) || !ability.can(action, candidate, field)) return false

  return ability
    .rulesFor(action, subject, field)
    .some(
      rule =>
        !rule.inverted && rule.matchesConditions(oldRecord) && rule.matchesConditions(candidate)
    )
}

export const canViewNavMenuHeading = () => {
  return true
}

export const canViewNavMenuGroup = item => {
  const hasAnyVisibleChild =
    Array.isArray(item.children) &&
    item.children.some(child => {
      if (Array.isArray(child.children) && child.children.length > 0) {
        return canViewNavMenuGroup(child)
      }

      return canPage(child.page, child)
    })

  const canViewThisGroup = item.page ? canPage(item.page, item) : false
  const canView = canViewThisGroup || hasAnyVisibleChild

  log.debug(`canViewNavMenuGroup -> ${item.title} ${canView ? 'can view' : 'cannot view'}`, item)

  return canView
}

export function canPageMeta(meta, identifier = '') {
  if (!meta) {
    log.warn(`canPageMeta -> no meta set ${identifier}`)

    return false
  }

  const subject = meta?.subject
  if (!subject) {
    log.warn(`canPageMeta ->  ${identifier} no subject set`)

    return false
  }

  if (meta.action) return can(meta.action, subject)

  const parentSubject = subject
  const parentAction = getAction(parentSubject)
  const canParent = can(parentAction, parentSubject)
  if (canParent) {
    log.debug(`canPageMeta ->  ${identifier} can with parent permission ${parentSubject}`)

    return true
  }

  const action = getAction(subject)
  if (!action) {
    log.warn(`canPageMeta ->  ${identifier} no action found for ${subject}`, meta)

    return false
  }

  return can(action, subject)
}

export const canPage = (Page, item = {}) => {
  const pageTitle = Page?.title ?? Page?.Title
  if (!Page) {
    let itemTitle = item.title ?? 'N/A'
    log.warn(`canPage -> no page set ${pageTitle ?? itemTitle}`)

    return false
  }

  const meta = Page?.Meta ?? Page?.meta

  return canPageMeta(meta, pageTitle)
}

function getAction(subject) {
  const auth = useAuthStore()
  const permissions = auth.getPermissions()

  if (!permissions) {
    log.warn(`getAction -> no permissions found for ${subject}`)

    return null
  }

  const matchedPermission = permissions.find(p => p.subject === subject)

  return matchedPermission?.action ?? null
}
